import { html, LitElement, type PropertyValueMap, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { GameContent, type LobbyGame, TileType } from '../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import { addMetaPreloadLink, updateRobotsTag } from '../../_ui-core_/base/package/util/html-helper';
import App from '../../app';
import type { FAQ } from '../../app/package/base/cms/package/cms-domain';
import { HeaderOptionsEventName } from '../../app/package/base/router/router-types/sidebar-types';
import { TrackingEventSource } from '../../app/package/base/service/tracking/tracking-service';
import type { BasePageHeaderOptions } from '../../page-templates/base-page/base-page-header';
import placeholderStyles from '../../styles/placeholder.css?inline';
import style from './game-info-page.css?inline';
import '../../_ui-core_/components/ui-game-info/ui-game-info';
import '../../_ui-core_/components/ui-spinner/ui-spinner';

const CName = 'game-info-page';

/**
 * @fires updateHeader - Event fired when the header should be updated
 */
@customElement(CName)
export class GameInfoPage extends LitElement {
  static readonly styles = [unsafeCSS(style), unsafeCSS(placeholderStyles)];

  @property({ type: String }) slug: string;

  private _gameInfoTaskId: number | null = null;
  private _loadAbortController: AbortController | null = null;

  @state() private _game: GameContent;
  @state() private _faq: FAQ[];
  @state() private _similar: LobbyGame[] | undefined = undefined;
  @state() private _maintenance = false;
  @state() private _headerOptions: BasePageHeaderOptions = {
    title: '',
    isGoBackShown: true,
    isFavoriteShown: true, // Will be updated with value of app settings `supportFavorites`
    trackingSource: TrackingEventSource.GAME_INFO_PAGE,
  };

  willUpdate(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (changedProperties.has('slug')) {
      this._loadGameData(this.slug);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._addRenderTask();
    this._updateFavoritesOption();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Abort in-flight data loading to release closure references to this element
    this._loadAbortController?.abort();
    this._loadAbortController = null;
    this._removeRenderTask();
  }

  render() {
    if (this._game === undefined) {
      return html`<div class="loading"><ui-spinner></ui-spinner></div>`;
    }

    return html`
      <main>
        <ui-game-info
          class="game-info"
          .game=${this._game}
          .faq=${this._faq}
          .similarGames=${this._similar}
          .maintenance=${this._maintenance}
        ></ui-game-info>
      </main>
    `;
  }

  private _updateFavoritesOption() {
    const isFavoritesEnabled = App.instance.appSettings.supportFavorites;
    this._headerOptions.isFavoriteShown = isFavoritesEnabled;
  }

  private _addRenderTask(): void {
    // Skip in the desktop slide-in: the render task would hide the background
    // page (main-slot + footer), while the sidebar itself is not gated by it.
    if (this._gameInfoTaskId === null && !window.$app.router.getIsSidebarOpen()) {
      this._gameInfoTaskId = window.$app.renderService.addTask('game-info');
    }
  }

  private _removeRenderTask(): void {
    if (this._gameInfoTaskId !== null) {
      this._gameInfoTaskId = window.$app.renderService.removeTask(this._gameInfoTaskId);
    }
  }

  private async _loadGameData(slug: string) {
    // Abort any previous in-flight load (e.g. rapid slug changes while still connected)
    this._loadAbortController?.abort();
    const controller = new AbortController();
    this._loadAbortController = controller;

    this._similar = undefined; // IGM-765 reset the array to ensure embla carousel recognizes the update

    try {
      const game = await App.instance.content.getGameInfo(undefined, slug, TileType.L1);
      if (controller.signal.aborted) return;

      this._preloadMedia(game);

      const gameDetails = GameContent.fromGameDetails(game);
      const faq = await App.instance.content.getFaq(gameDetails.faqIds);
      if (controller.signal.aborted) return;

      this._headerOptions = {
        ...this._headerOptions,
        title: game.title,
        gameId: game.id,
        provider: gameDetails.studio,
      };
      dispatchCustomEvent(this, HeaderOptionsEventName, this._headerOptions);

      // Primary content ready — make page visible
      this._game = gameDetails;
      this._faq = faq;

      const robots = gameDetails.noIndex ? 'noindex' : window.$app.appConfig.metaRobotsDefault;
      updateRobotsTag(robots);

      // Below-fold / non-LCP content loads after page is visible
      this._getSimilarGames(gameDetails.id).then((similar) => {
        if (controller.signal.aborted) return;
        this._similar = similar;
      });

      App.instance.content.getGame(game.id).then((g) => {
        if (controller.signal.aborted) return;
        this._maintenance = g.maintenance ?? false;
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      // Redirect to 404 page if game info is not found
      window.$app.logger.warn(`Error fetching game info for slug '${slug}'`, err);
      App.instance.router.navigateToNotFound();
      return;
    } finally {
      if (!controller.signal.aborted) {
        // Wait for Lit to commit the content before revealing the page
        await this.updateComplete;
        this._removeRenderTask();
        if (!controller.signal.aborted) {
          App.instance.router.scrollToTop();
        }
      }
    }
  }

  private _preloadMedia(game: any) {
    if (!game.vid && game.img) {
      addMetaPreloadLink(game.img, 'image', true);
    }
  }

  private async _getSimilarGames(gameId: string): Promise<LobbyGame[] | undefined> {
    try {
      return await App.instance.content.getGameSimilar(gameId, TileType.P1);
    } catch (err) {
      window.$app.logger.warn(`Failed to fetch similar games for gameId '${gameId}'`, err);
      return undefined;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: GameInfoPage;
  }
}
